// src/pages/DashboardHomePage.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import './UserDashboard.css';

const DashboardHomePage = () => {
    const { user } = useAuth();
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [tomorrowsAddons, setTomorrowsAddons] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user || !user.uid) {
            setLoading(false);
            return;
        }

        const fetchSubscriptionData = async () => {
            try {
                const subRef = doc(db, 'user_subscriptions', user.uid);
                const subSnap = await getDoc(subRef);
                if (subSnap.exists()) {
                    const subData = subSnap.data();
                    const dates = subData.selectedDates || [];
                    if (dates.length > 0) {
                        setSubscriptionInfo({
                            startDate: new Date(dates[0]).toLocaleDateString('en-GB'),
                            endDate: new Date(dates[dates.length - 1]).toLocaleDateString('en-GB'),
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching subscription info:", error);
            }
        };

        const fetchTomorrowsAddons = async () => {
            try {
                const today = new Date();
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);
                
                const year = tomorrow.getFullYear();
                const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
                const day = String(tomorrow.getDate()).padStart(2, '0');
                const tomorrowString = `${year}-${month}-${day}`;

                const q = query(
                    collection(db, "Addon_Approved"),
                    where("customerId", "==", user.uid),
                    where("deliveryDate", "==", tomorrowString)
                );

                const querySnapshot = await getDocs(q);
                const addons = [];
                querySnapshot.forEach(doc => {
                    addons.push(...doc.data().items);
                });
                setTomorrowsAddons(addons);

            } catch (error) {
                console.error("Error fetching tomorrow's add-ons:", error);
            }
        };

        const fetchAllData = async () => {
            setLoading(true);
            await Promise.all([fetchSubscriptionData(), fetchTomorrowsAddons()]);
            setLoading(false);
        };
        
        fetchAllData();

    }, [user]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard-grid">
            {/* Card 1: Subscription Period */}
            <div className="dashboard-card subscription-card">
                <div className="card-header">
                    <div className="card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                        </svg>
                    </div>
                    <h3>Subscription Period</h3>
                </div>
                <div className="card-content">
                    {subscriptionInfo ? (
                        <div className="subscription-dates">
                            <div className="date-item">
                                <span className="date-label">Start Date</span>
                                <span className="date-value">{subscriptionInfo.startDate}</span>
                            </div>
                            <div className="date-item">
                                <span className="date-label">End Date</span>
                                <span className="date-value">{subscriptionInfo.endDate}</span>
                            </div>
                            
                        </div>
                    ) : (
                        <div className="no-subscription">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            <p>No active subscription found.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Card 2: Tomorrow's Add-Ons */}
            <div className="dashboard-card addons-card">
                <div className="card-header">
                    <div className="card-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V7H1v10h22v-6c0-2.21-1.79-4-4-4z"/>
                        </svg>
                    </div>
                    <h3>Tomorrow's Add-on Deliveries</h3>
                    <span className="badge">{tomorrowsAddons.length}</span>
                </div>
                <div className="card-content">
                    {tomorrowsAddons.length > 0 ? (
                        <ul className="addon-list">
                            {tomorrowsAddons.map((item, index) => (
                                <li key={index} className="addon-item">
                                    <div className="addon-info">
                                        <span className="addon-name">{item.foodItemName}</span>
                                        <span className="addon-quantity">x{item.quantity}</span>
                                    </div>
                                    <div className="addon-status pending">Scheduled</div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="no-addons">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            <p>No add-ons scheduled for tomorrow.</p>
                        </div>
                    )}
                </div>
                {tomorrowsAddons.length > 0 && (
                    <div className="card-footer">
                        <button className="view-details-btn">View Delivery Details</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardHomePage;